/**
 * KotPrintJobsApi.ts — API file d'impression KOT idempotente (lot T1, chantier 2).
 *
 * Garantit l'idempotence du split multi-fonction KOT (limite connue PrintingApi:364-368)
 * et l'anti-double impression multi-devices via un claim atomique.
 *
 * NON FISCAL : cette API ne touche jamais les tables fiscales.
 * Payload = articles/quantités/notes de préparation — aucun PII (4.A.15).
 *
 * Toutes les méthodes retournent Result<T, AppError>.
 *
 * Invariants :
 *   4.A.10 — tenant_id jamais dans les inputs ; DEFAULT get_my_tenant_id() + RLS.
 *   4.A.15 — aucun PII loggé ni dans le payload.
 *   4.B.2  — seule interface entre features/hooks et Supabase pour kot_print_jobs.
 *   4.B.4  — 0 any, types stricts.
 */

import { supabase } from '@/integrations/supabase/client';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase, authError, notFoundError } from '@/api/errors';
import type { AppError } from '@/api/errors';
import { firstZodIssueToValidationError } from './errors';
import { createKotPrintJobsBatchSchema } from './types';
import type {
  KotPrintJob,
  KotPrintJobRow,
  CreateKotPrintJobInput,
  ListPendingJobsFilters,
} from './types';

export const KotPrintJobsApi = {
  /**
   * Insère un batch de jobs d'impression de façon idempotente.
   *
   * ON CONFLICT DO NOTHING (via upsert ignoreDuplicates) : si un job avec la
   * même (tenant_id, kot_key, printer_function) existe déjà, il est silencieusement
   * ignoré — le retry avec la même clé est safe. Les jobs déjà créés ne sont pas
   * re-créés ni modifiés.
   *
   * Retourne les jobs effectivement insérés (les doublons ignorés ne sont pas dans
   * le résultat — c'est le comportement de PostgREST avec ON CONFLICT DO NOTHING).
   */
  async insertJobsIdempotent(
    jobs: CreateKotPrintJobInput[],
  ): Promise<Result<KotPrintJob[], AppError>> {
    const parsed = createKotPrintJobsBatchSchema.safeParse(jobs);
    if (!parsed.success) {
      return err(firstZodIssueToValidationError(parsed.error, 'Données jobs KOT invalides'));
    }

    const { data, error } = await supabase
      .from('kot_print_jobs')
      .upsert(parsed.data as KotPrintJobRow[], { ignoreDuplicates: true })
      .select('*');

    if (error) return err(appErrorFromSupabase(error, 'KotPrintJobsApi.insertJobsIdempotent'));
    return ok((data ?? []) as KotPrintJobRow[]);
  },

  /**
   * Tente d'acquérir le claim sur un job — pattern compare-and-swap.
   *
   * Stratégie en deux passes (chacune atomique au niveau DB) :
   *   1. Job en statut 'pending' OU 'failed' → claim direct.
   *      ('failed' reclamable immédiatement : un retry doit pouvoir ré-imprimer
   *      le bon en échec — lot 4 chantier CHR. Avant ce fix, un job failed
   *      n'était reclamable par personne et le retry le sautait en silence.)
   *   2. Job en statut 'claimed' depuis > 60 s (TTL expiré) → reclaim.
   *
   * Retourne :
   *   ok(KotPrintJob) si le claim est acquis.
   *   ok(null)        si un autre device a gagné la course (pas d'erreur — cas normal).
   *   err(AppError)   si une erreur DB ou auth s'est produite.
   *
   * Invariant 4.A.10 : tenant_id n'est PAS passé ici — RLS enforce l'isolation.
   * claimed_by = auth.uid() côté client (champ d'audit non fiscal, pas de FK sécuritaire).
   */
  async claimJob(id: string): Promise<Result<KotPrintJob | null, AppError>> {
    const { data: authData, error: authErr } = await supabase.auth.getUser();
    if (authErr || !authData.user) {
      return err(authError('no_jwt'));
    }

    const now = new Date().toISOString();
    // TTL de claim : 60 secondes. Un job claimed depuis plus longtemps est reclamable.
    const threshold = new Date(Date.now() - 60_000).toISOString();
    const claimPayload = {
      status: 'claimed',
      claimed_by: authData.user.id,
      claimed_at: now,
    };

    // Passe 1 : job pending (jamais imprimé) ou failed (retry) → claim atomique.
    const { data: d1, error: e1 } = await supabase
      .from('kot_print_jobs')
      .update(claimPayload)
      .eq('id', id)
      .in('status', ['pending', 'failed'])
      .select('*');

    if (e1) return err(appErrorFromSupabase(e1, 'KotPrintJobsApi.claimJob'));
    if (d1 && d1.length > 0) return ok(d1[0] as KotPrintJobRow);

    // Passe 2 : reclaim d'un job claimed expiré (> 60 s sans printed/failed).
    const { data: d2, error: e2 } = await supabase
      .from('kot_print_jobs')
      .update(claimPayload)
      .eq('id', id)
      .eq('status', 'claimed')
      .lt('claimed_at', threshold)
      .select('*');

    if (e2) return err(appErrorFromSupabase(e2, 'KotPrintJobsApi.claimJob'));
    // Résultat vide = un autre device a gagné la course. ok(null) = cas normal.
    return ok(d2 && d2.length > 0 ? (d2[0] as KotPrintJobRow) : null);
  },

  /**
   * Marque un job comme imprimé avec succès.
   *
   * À appeler immédiatement après l'impression physique réussie.
   * Le job passé en 'printed' ne sera plus reclamé par aucun relais.
   */
  async markPrinted(id: string): Promise<Result<KotPrintJob, AppError>> {
    const { data, error } = await supabase
      .from('kot_print_jobs')
      .update({ status: 'printed', printed_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single();

    if (error) return err(appErrorFromSupabase(error, 'KotPrintJobsApi.markPrinted'));
    if (!data) return err(notFoundError('kot_print_job', id));
    return ok(data as KotPrintJobRow);
  },

  /**
   * Marque un job comme échoué et incrémente attempt_count.
   *
   * Deux requêtes séquentielles (fetch + update) : safe car le job est claiment
   * par le device appelant — aucune race sur attempt_count.
   * Le job repasse en 'failed' et devient reclamable via un autre device ou retry.
   *
   * Note : PrintingApi incrémente via cette méthode, pas via un UPDATE libre.
   */
  async markFailed(id: string): Promise<Result<KotPrintJob, AppError>> {
    // Récupération du compte actuel (le job est claiment — pas de race sur ce champ).
    const { data: current, error: fetchErr } = await supabase
      .from('kot_print_jobs')
      .select('attempt_count')
      .eq('id', id)
      .single();

    if (fetchErr) return err(appErrorFromSupabase(fetchErr, 'KotPrintJobsApi.markFailed'));
    if (!current) return err(notFoundError('kot_print_job', id));

    const { data, error } = await supabase
      .from('kot_print_jobs')
      .update({ status: 'failed', attempt_count: current.attempt_count + 1 })
      .eq('id', id)
      .select('*')
      .single();

    if (error) return err(appErrorFromSupabase(error, 'KotPrintJobsApi.markFailed'));
    if (!data) return err(notFoundError('kot_print_job', id));
    return ok(data as KotPrintJobRow);
  },

  /**
   * Liste les jobs en attente d'impression pour un tenant.
   *
   * Utilisé par le relay hook (useKotPrintRelay, lot T6) et par la tablette
   * pour afficher l'état d'un envoi cuisine.
   *
   * Le filtre tenant_id est belt-and-suspenders : RLS enforce l'isolation serveur.
   * L'index idx_kot_print_jobs_tenant_status garantit la performance.
   */
  async listPendingJobs(filters: ListPendingJobsFilters): Promise<Result<KotPrintJob[], AppError>> {
    const { data, error } = await supabase
      .from('kot_print_jobs')
      .select('*')
      .eq('tenant_id', filters.tenantId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true });

    if (error) return err(appErrorFromSupabase(error, 'KotPrintJobsApi.listPendingJobs'));
    return ok((data ?? []) as KotPrintJobRow[]);
  },

  /**
   * Liste tous les jobs pour une clé KOT donnée, tous statuts confondus.
   *
   * Utilisé par `printGroupsWithPersistence` (lot T2) après insertion idempotente :
   * récupère l'id et le statut courant de chaque job pour décider de le claimer
   * ou de le sauter (statut 'printed').
   *
   * Invariant 4.A.10 : tenant_id non passé en paramètre — RLS enforce l'isolation.
   */
  async listJobsByKotKey(kotKey: string): Promise<Result<KotPrintJob[], AppError>> {
    const { data, error } = await supabase
      .from('kot_print_jobs')
      .select('*')
      .eq('kot_key', kotKey)
      .order('created_at', { ascending: true });

    if (error) return err(appErrorFromSupabase(error, 'KotPrintJobsApi.listJobsByKotKey'));
    return ok((data ?? []) as KotPrintJobRow[]);
  },
} as const;
